import { LuEllipsis } from '@react-icons/all-files/lu/LuEllipsis'
import * as React from 'react'

import type { GROUP_TYPE } from '@disco/constants/GroupTypes'
import type { PaneType } from '@disco/constants/PaneTypes'
import type { SORT, SORT_DIRECTION } from '@disco/constants/SortTypes'
import type { ViewType } from '@disco/constants/ViewTypes'
import type { SimSearchQuery } from '@disco/state-management/reducers/search'

import type { Group, GroupId } from 'types/group'
import type { PopoverItemType } from 'types/ui'

import { DropdownMenu, IconButton } from 'components/ds'
import {
  channelViewOptions,
  playlistOptions,
  tagFilterOption,
  trackOptions,
} from 'components/explorer/pane/search-header/select-options'
import EllipsisMenu from 'components/ui/ellipsis-menu'

import GroupMenuBody from './group-menu-body'
import { useGroupMenu } from './use-group-menu'

type Props = {
  pane?: {
    groupType: GROUP_TYPE
    groupId: GroupId
    artistSortParam: SORT | null | undefined
    artistSortDirection: SORT_DIRECTION | null | undefined
    trackSortParam: SORT | null | undefined
    trackSortDirection: SORT_DIRECTION | null | undefined
    playlistSortParam: SORT
    playlistSortDirection: SORT_DIRECTION
    view: ViewType | null | undefined
    type: PaneType
    index: number
    simSearch: SimSearchQuery | null | undefined
  }
  group: Group | null | undefined
  groupDepth?: number
  onViewSwitch?: (view: ViewType | null | undefined, value?: string) => unknown
  sortingOptions?: PopoverItemType[]
  right?: boolean
  compact?: boolean
}

const GroupItemActions: React.FC<Props> = ({
  pane,
  group,
  groupDepth,
  onViewSwitch,
  sortingOptions,
  right,
  compact,
}) => {
  const { options, handleMenuAction, handleMenuOpen, dialogs } = useGroupMenu({
    group,
    groupDepth,
    pane,
    sortingOptions,
  })

  if (!group) return null

  const isSidebarContext = compact && !pane

  const menu =
    options.length > 0 ? (
      <div className="ExplorerPane_Header_Ellipsis">
        {isSidebarContext ? (
          <DropdownMenu.Root
            onOpenChange={open => {
              if (open) handleMenuOpen()
            }}
          >
            <DropdownMenu.Trigger>
              <IconButton variant="ghost" color="gray" size="1">
                <LuEllipsis size={16} />
              </IconButton>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
              <GroupMenuBody
                options={options}
                onOptionClick={handleMenuAction}
              />
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        ) : (
          <EllipsisMenu
            buttonSize={compact ? 'compact' : 'default'}
            right={right}
            popoverClassName="ExplorerPane_Context_Menu"
            icon="ellipsis-vertical"
            hasArrow={false}
            options={options}
            onOpen={handleMenuOpen}
            onOptionClick={(value: string) => {
              if (
                trackOptions.map(o => o.value).includes(value) ||
                playlistOptions.map(o => o.value).includes(value)
              ) {
                if (value !== tagFilterOption.value) {
                  if (pane && onViewSwitch) onViewSwitch(pane.view, value)
                }
                return
              }

              if (channelViewOptions.map(o => o.value).includes(value)) {
                const channelViewType = value as any as ViewType
                if (onViewSwitch) onViewSwitch(channelViewType)
                return
              }

              handleMenuAction(value)
            }}
          />
        )}
      </div>
    ) : null

  return (
    <>
      {menu}
      {dialogs}
    </>
  )
}

export default GroupItemActions
